import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { BrandingProvider } from './context/BrandingContext';
import { Toaster } from './components/ui/sonner';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import Login from './pages/Login';
import Landing from './pages/Landing';
import Register from './pages/Register';
import TrialPending from './pages/TrialPending';
import ForgotPassword from './pages/ForgotPassword';
import RoleSelection from './pages/RoleSelection';
import Dashboard from './pages/Dashboard';
import Schedule from './pages/Schedule';
import Medics from './pages/Medics';
import Shifts from './pages/Shifts';
import Expenses from './pages/Expenses';
import Reports from './pages/Reports';
import AuditLogs from './pages/AuditLogs';
import Settings from './pages/Settings';
import Contracts from './pages/Contracts';
import JobTypes from './pages/JobTypes';
import ShiftRequests from './pages/ShiftRequests';
import Compliance from './pages/Compliance';
import LiveMap from './pages/LiveMap';
import MedicPortal from './pages/portal/MedicPortal';
import MedicAvailability from './pages/portal/MedicAvailability';
import MedicShifts from './pages/portal/MedicShifts';
import MedicExpenses from './pages/portal/MedicExpenses';
import MedicDocuments from './pages/portal/MedicDocuments';
import MedicProfile from './pages/portal/MedicProfile';
import OpenShifts from './pages/portal/OpenShifts';
import ShiftDetails from './pages/portal/ShiftDetails';
import Leave from './pages/Leave';
import MedicLeave from './pages/portal/MedicLeave';
import HelpSupport from './pages/HelpSupport';
import Platform from './pages/Platform';
import AcceptInvite from './pages/AcceptInvite';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import './App.css';

// Normalize role for backwards compatibility (team_leader -> manager)
const normalizeRole = (role) => {
  if (role === 'team_leader') return 'manager';
  return role;
};

// Get effective role (respects impersonation and session role)
const getEffectiveRole = (user, isImpersonating, effectiveRole, sessionRole) => {
  if (isImpersonating && effectiveRole) {
    return normalizeRole(effectiveRole);
  }
  if (sessionRole) {
    return normalizeRole(sessionRole);
  }
  return normalizeRole(user?.role);
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, isImpersonating, effectiveRole, sessionRole, requiresRoleSelection } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#1ABC9C]/20 border-t-[#1ABC9C] rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Redirect to role selection if user has multiple roles and hasn't selected one
  if (requiresRoleSelection) {
    return <Navigate to="/select-role" replace />;
  }

  // Use effective role for permission checks (respects impersonation and session role)
  const currentRole = getEffectiveRole(user, isImpersonating, effectiveRole, sessionRole);

  // Normalize allowed roles for comparison
  const normalizedAllowedRoles = allowedRoles?.map(r => normalizeRole(r)) || [];
  // Also include backwards compatibility roles
  if (normalizedAllowedRoles.includes('manager')) {
    normalizedAllowedRoles.push('team_leader');
  }

  if (allowedRoles && !normalizedAllowedRoles.includes(currentRole)) {
    // Redirect based on effective role
    if (currentRole === 'medic') {
      return <Navigate to="/portal" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return <Layout><ErrorBoundary>{children}</ErrorBoundary></Layout>;
};

// Public Route Component (redirects if already logged in)
const PublicRoute = ({ children }) => {
  const { user, loading, isImpersonating, effectiveRole, sessionRole, requiresRoleSelection } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#1ABC9C]/20 border-t-[#1ABC9C] rounded-full animate-spin" />
      </div>
    );
  }

  if (user) {
    // Redirect to role selection if needed
    if (requiresRoleSelection) {
      return <Navigate to="/select-role" replace />;
    }
    
    // Use effective role for redirect (respects impersonation and session role)
    const currentRole = getEffectiveRole(user, isImpersonating, effectiveRole, sessionRole);
    if (currentRole === 'medic') {
      return <Navigate to="/portal" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Role Selection Route (only for logged-in users who need to select a role)
const RoleSelectionRoute = ({ children }) => {  const { user, loading, requiresRoleSelection, sessionRole } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#1ABC9C]/20 border-t-[#1ABC9C] rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // If role already selected, redirect to appropriate page
  if (!requiresRoleSelection || sessionRole) {
    const role = normalizeRole(sessionRole || user?.role);
    if (role === 'medic') {
      return <Navigate to="/portal" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Platform Console guard — allows ONLY users holding the platform_admin flag.
// Renders standalone (no tenant <Layout>) so the console is visually isolated.
const PlatformRoute = () => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0B0F] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-amber-500/20 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_platform_admin) return <Navigate to="/dashboard" replace />;
  return <Platform />;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } 
      />
      <Route 
        path="/register" 
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        } 
      />
      <Route 
        path="/forgot-password" 
        element={<ForgotPassword />} 
      />
      <Route
        path="/trial-pending"
        element={<TrialPending />}
      />

      {/* Invitation accept — unauthenticated: the token is the auth. */}
      <Route path="/invitations/accept" element={<AcceptInvite />} />

      {/* Stripe Checkout return URLs — public pages, no auth required.
          Status is confirmed server-side via /api/payments/status/{id}. */}
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />

      {/* Role Selection Route */}
      <Route 
        path="/select-role" 
        element={
          <RoleSelectionRoute>
            <RoleSelection />
          </RoleSelectionRoute>
        } 
      />

      {/* Admin/Ops/Manager Routes */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager', 'team_leader', 'ops_coordinator', 'standard']}>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/schedule" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager', 'team_leader', 'ops_coordinator', 'standard']}>
            <Schedule />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/leave" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager', 'team_leader', 'ops_coordinator', 'standard']}>
            <Leave />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/medics" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager', 'team_leader', 'ops_coordinator', 'standard']}>
            <Medics />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/shifts" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager', 'team_leader', 'ops_coordinator', 'standard']}>
            <Shifts />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/expenses" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager', 'team_leader', 'ops_coordinator', 'standard']}>
            <Expenses />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/reports" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager', 'team_leader']}>
            <Reports />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/audit-logs" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager', 'team_leader']}>
            <AuditLogs />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/settings" 
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <Settings />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/contracts" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager', 'team_leader', 'ops_coordinator', 'standard']}>
            <Contracts />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/job-types" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager', 'team_leader', 'ops_coordinator', 'standard']}>
            <JobTypes />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/shift-requests" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager', 'team_leader', 'ops_coordinator', 'standard']}>
            <ShiftRequests />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/compliance" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager', 'team_leader', 'ops_coordinator', 'standard']}>
            <Compliance />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/live-map" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager', 'team_leader', 'ops_coordinator']}>
            <LiveMap />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/help" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager', 'team_leader', 'ops_coordinator', 'standard', 'medic']}>
            <HelpSupport />
          </ProtectedRoute>
        } 
      />

      {/* Medic Portal Routes */}
      <Route 
        path="/portal" 
        element={
          <ProtectedRoute allowedRoles={['medic']}>
            <MedicPortal />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/portal/shifts" 
        element={
          <ProtectedRoute allowedRoles={['medic']}>
            <MedicShifts />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/portal/shifts/:id" 
        element={
          <ProtectedRoute allowedRoles={['medic']}>
            <ShiftDetails />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/portal/availability" 
        element={
          <ProtectedRoute allowedRoles={['medic']}>
            <MedicAvailability />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/portal/leave" 
        element={
          <ProtectedRoute allowedRoles={['medic']}>
            <MedicLeave />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/portal/expenses" 
        element={
          <ProtectedRoute allowedRoles={['medic']}>
            <MedicExpenses />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/portal/documents" 
        element={
          <ProtectedRoute allowedRoles={['medic']}>
            <MedicDocuments />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/portal/profile" 
        element={
          <ProtectedRoute allowedRoles={['medic']}>
            <MedicProfile />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/portal/open-shifts" 
        element={
          <ProtectedRoute allowedRoles={['medic']}>
            <OpenShifts />
          </ProtectedRoute>
        } 
      />

      {/* Default Redirect */}
      <Route path="/" element={<Landing />} />
      {/* Platform Admin Console (standalone — no tenant Layout) */}
      <Route path="/platform" element={<PlatformRoute />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrandingProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-center" richColors toastOptions={{ style: { marginTop: '60px' } }} />
        </BrowserRouter>
      </BrandingProvider>
    </AuthProvider>
  );
}

export default App;
